import { UserRole, AgeGroup } from '@/types/user';

// Define all possible permissions in the system
export type Permission = 
  | 'view_dashboard'
  | 'create_team'
  | 'manage_team'
  | 'manage_players'
  | 'view_players'
  | 'create_venue_listing'
  | 'manage_venue'
  | 'upload_content'
  | 'tag_content'
  | 'view_analytics'
  | 'manage_bookings'
  | 'update_scores'
  | 'manage_games'
  | 'approve_child_activities'
  | 'manage_child_accounts'
  | 'follow_teams'
  | 'comment_content'
  | 'view_schedules'
  | 'create_chat_groups'
  | 'join_chat_groups'
  | 'view_public_profiles'
  | 'manage_own_profile'
  | 'view_team_roster'
  | 'schedule_practices'
  | 'send_team_messages'
  | 'book_venues'
  | 'create_events'
  | 'manage_finances'
  | 'access_premium_features'
  | 'run_system_tests'
  | 'view_test_results'
  | 'manage_all_users'
  | 'view_system_analytics';

// Role permission mappings - Updated for free vs paid accounts
const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  // FREE ACCOUNTS
  player: [
    'view_dashboard',
    'view_schedules',
    'join_chat_groups',
    'view_public_profiles',
    'manage_own_profile',
    'view_team_roster',
    'follow_teams',
    'comment_content'
  ],
  fan: [
    'view_dashboard',
    'follow_teams',
    'comment_content',
    'join_chat_groups',
    'view_public_profiles',
    'manage_own_profile',
    'view_schedules'
  ],
  parent: [
    'view_dashboard',
    'approve_child_activities',
    'manage_child_accounts',
    'join_chat_groups',
    'view_public_profiles',
    'manage_own_profile',
    'view_schedules',
    'follow_teams'
  ],
  child: [
    'view_dashboard',
    'view_schedules',
    'join_chat_groups',
    'view_public_profiles',
    'manage_own_profile',
    'view_team_roster',
    'follow_teams'
  ],
  support: [
    'view_dashboard',
    'update_scores',
    'manage_games',
    'join_chat_groups',
    'view_public_profiles',
    'manage_own_profile',
    'view_schedules',
    'view_team_roster'
  ],
  scorekeeper: [
    'view_dashboard',
    'update_scores',
    'manage_games',
    'join_chat_groups',
    'view_public_profiles',
    'manage_own_profile',
    'view_schedules',
    'view_team_roster',
    'follow_teams'
  ],
  creative: [
    'view_dashboard',
    'upload_content',
    'tag_content',
    'join_chat_groups',
    'view_public_profiles',
    'manage_own_profile',
    'follow_teams',
    'comment_content'
  ],
  
  // PAID ACCOUNTS
  coach: [
    'view_dashboard',
    'manage_team',
    'manage_players',
    'view_players',
    'view_schedules',
    'schedule_practices',
    'create_chat_groups',
    'join_chat_groups',
    'view_public_profiles',
    'manage_own_profile',
    'view_team_roster',
    'send_team_messages',
    'view_analytics',
    'manage_games',
    'create_events',
    'access_premium_features'
  ],
  'team-owner': [
    'view_dashboard',
    'create_team',
    'manage_team',
    'manage_players',
    'view_players',
    'view_schedules',
    'create_chat_groups',
    'join_chat_groups',
    'view_public_profiles',
    'manage_own_profile',
    'view_team_roster',
    'send_team_messages',
    'view_analytics',
    'manage_games',
    'manage_bookings',
    'book_venues',
    'create_events',
    'manage_finances',
    'access_premium_features'
  ],
  venue: [
    'view_dashboard',
    'create_venue_listing',
    'manage_venue',
    'manage_games',
    'manage_bookings',
    'view_schedules',
    'join_chat_groups',
    'view_public_profiles',
    'manage_own_profile',
    'view_analytics',
    'create_events',
    'manage_finances',
    'access_premium_features'
  ],
  
  // SUBSCRIPTION-BASED ACCOUNT
  agent: [
    'view_dashboard',
    'view_public_profiles',
    'manage_own_profile',
    'view_schedules',
    'join_chat_groups',
    'view_analytics',
    'access_premium_features'
  ],
  
  // ADMIN ACCOUNT - FULL ACCESS
  admin: [
    'view_dashboard',
    'create_team',
    'manage_team',
    'manage_players',
    'view_players',
    'create_venue_listing',
    'manage_venue',
    'upload_content',
    'tag_content',
    'view_analytics',
    'manage_bookings',
    'update_scores',
    'manage_games',
    'approve_child_activities',
    'manage_child_accounts',
    'follow_teams',
    'comment_content',
    'view_schedules',
    'create_chat_groups',
    'join_chat_groups',
    'view_public_profiles',
    'manage_own_profile',
    'view_team_roster',
    'schedule_practices',
    'send_team_messages',
    'book_venues',
    'create_events',
    'manage_finances',
    'access_premium_features',
    'run_system_tests',
    'view_test_results',
    'manage_all_users',
    'view_system_analytics'
  ]
};

// Age-based restrictions remain the same
const AGE_RESTRICTIONS: Record<AgeGroup, Permission[]> = {
  '1': ['view_dashboard', 'view_schedules', 'manage_own_profile', 'view_team_roster'],
  '2': ['view_dashboard', 'view_schedules', 'manage_own_profile', 'view_team_roster'],
  '3': ['view_dashboard', 'view_schedules', 'manage_own_profile', 'view_team_roster'],
  '4': ['view_dashboard', 'view_schedules', 'manage_own_profile', 'view_team_roster'],
  '5': ['view_dashboard', 'view_schedules', 'manage_own_profile', 'view_team_roster'],
  '6': ['view_dashboard', 'view_schedules', 'manage_own_profile', 'view_team_roster'],
  '7': ['view_dashboard', 'view_schedules', 'manage_own_profile', 'view_team_roster'],
  '8': ['view_dashboard', 'view_schedules', 'manage_own_profile', 'view_team_roster'],
  '9': ['view_dashboard', 'view_schedules', 'manage_own_profile', 'view_team_roster'],
  '10': ['view_dashboard', 'view_schedules', 'manage_own_profile', 'view_team_roster'],
  '11': ['view_dashboard', 'view_schedules', 'manage_own_profile', 'view_team_roster'],
  '12': ['view_dashboard', 'view_schedules', 'manage_own_profile', 'view_team_roster'],
  '13': ['view_dashboard', 'view_schedules', 'manage_own_profile', 'view_team_roster'],
  '13-and-under': ['view_dashboard', 'view_schedules', 'manage_own_profile', 'view_team_roster'],
  '14-17': [
    'view_dashboard',
    'view_schedules',
    'join_chat_groups',
    'view_public_profiles',
    'manage_own_profile',
    'view_team_roster',
    'follow_teams',
    'comment_content',
    'upload_content',
    'tag_content',
    'update_scores'
  ],
  '18-plus': []
};

export const hasPermission = (
  userRole: UserRole,
  userAgeGroup: AgeGroup,
  permission: Permission
): boolean => {
  const rolePermissions = ROLE_PERMISSIONS[userRole] || [];
  if (!rolePermissions.includes(permission)) {
    return false;
  }

  if (userAgeGroup !== '18-plus') {
    const agePermissions = AGE_RESTRICTIONS[userAgeGroup] || [];
    return agePermissions.includes(permission);
  }

  return true;
};

export const getUserPermissions = (
  userRole: UserRole,
  userAgeGroup: AgeGroup
): Permission[] => {
  const rolePermissions = ROLE_PERMISSIONS[userRole] || [];
  
  if (userAgeGroup === '18-plus') {
    return rolePermissions;
  }

  const agePermissions = AGE_RESTRICTIONS[userAgeGroup] || [];
  return rolePermissions.filter(permission => agePermissions.includes(permission));
};

// Helper functions for account tier checks
export const isAccountFree = (userRole: UserRole): boolean => {
  const freeRoles: UserRole[] = ['player', 'fan', 'parent', 'child', 'support', 'scorekeeper', 'creative'];
  return freeRoles.includes(userRole);
};

export const isAccountPaid = (userRole: UserRole): boolean => {
  const paidRoles: UserRole[] = ['coach', 'team-owner', 'venue'];
  return paidRoles.includes(userRole);
};

export const canCreateChatGroups = (userRole: UserRole, userAgeGroup: AgeGroup): boolean => {
  return hasPermission(userRole, userAgeGroup, 'create_chat_groups');
};

export const canManageTeam = (userRole: UserRole, userAgeGroup: AgeGroup): boolean => {
  return hasPermission(userRole, userAgeGroup, 'manage_team');
};

export const canUploadContent = (userRole: UserRole, userAgeGroup: AgeGroup): boolean => {
  return hasPermission(userRole, userAgeGroup, 'upload_content');
};

export const canManageVenue = (userRole: UserRole, userAgeGroup: AgeGroup): boolean => {
  return hasPermission(userRole, userAgeGroup, 'manage_venue');
};

export const canAccessPremiumFeatures = (userRole: UserRole, userAgeGroup: AgeGroup): boolean => {
  return hasPermission(userRole, userAgeGroup, 'access_premium_features');
};
